import React, { useSyncExternalStore, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "./components/LanguageSwitcher";
import { Logo } from "./components/Logo";
import "./index.css";
import { Impressum } from "./pages/Impressum";
import { Datenschutz } from "./pages/Datenschutz";
import { SkylinePrivacy } from "./pages/SkylinePrivacy";
import { SkylineTerms } from "./pages/SkylineTerms";
import { SkylineSupport } from "./pages/SkylineSupport";
import { ProductPage } from "./pages/ProductPage";
import { Products } from "./pages/Products";
import { ServicePage } from "./pages/ServicePage";
import { OfferingPage } from "./pages/OfferingPage";
import { About } from "./pages/About";
import { Contact } from "./pages/Contact";
import { OpenSource } from "./pages/OpenSource";
import { Work } from "./pages/Work";
import { Stack } from "./pages/Stack";
import { Blog } from "./pages/Blog";
import { Careers } from "./pages/Careers";
import { Pricing } from "./pages/Pricing";
import { NotFound } from "./pages/NotFound";
import {
  ProductsSection,
  CateSection,
  WhySection,
  HowSection,
  WhoSection,
  PricingTeaserSection,
  CtaSection,
  HomeFooter,
} from "./components/HomeSections";
import { FullDivider } from "./pages/SubPage";

/**
 * When the AI_OVERLAY_ONLY gate is set on the server, it injects
 * `window.__CERO_OVERLAY_ONLY__ = true` before the app boots. The site stays a
 * full agency site, but stops selling website building/rework: the "Website
 * Rework" offering is hidden and the hero drops its rework wording.
 */
export const OVERLAY_ONLY =
  typeof window !== "undefined" &&
  (window as unknown as { __CERO_OVERLAY_ONLY__?: boolean }).__CERO_OVERLAY_ONLY__ === true;

const NAV_ITEMS = [
  { key: "products", href: "/products" },
  { key: "about", href: "/about" },
  { key: "pricing", href: "/pricing" },
  { key: "contact", href: "/contact" },
] as const;

function subscribe(cb: () => void) {
  window.addEventListener("popstate", cb);
  return () => window.removeEventListener("popstate", cb);
}

export function navigate(to: string) {
  window.history.pushState(null, "", to);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

type AiOverlayApi = {
  open?: () => void;
  ask?: (query: string) => void;
};

export function openAiOverlay(query?: string): boolean {
  const api = (window as unknown as { __overlay?: AiOverlayApi }).__overlay;
  if (query?.trim() && api?.ask) {
    api.ask(query.trim());
    return true;
  }
  if (api?.open) {
    api.open();
    return true;
  }
  return false;
}

export function openAiOverlayOrNavigate(query: string | undefined, fallbackPath: string) {
  if (!openAiOverlay(query)) navigate(fallbackPath);
}

function Nav({ currentPath }: { currentPath: string }) {
  const { t } = useTranslation("nav");
  const { t: tai } = useTranslation("ai");
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuIconKey, setMenuIconKey] = useState(0);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    setMenuOpen(false);
  }, [currentPath]);
  useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  const navOpaque = scrolled || menuOpen;

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50"
      style={{
        backgroundColor: navOpaque ? "#16171C" : "transparent",
        borderBottom: navOpaque ? "1px solid #27272a" : "1px solid transparent",
        transition: "background-color 200ms ease, border-color 200ms ease",
      }}
    >
      <div className="mx-auto px-5 sm:px-8 md:px-12 h-14 flex items-center justify-between gap-4">
        <a href="/" onClick={(e) => { e.preventDefault(); navigate("/"); }} className="flex items-center shrink-0">
          <Logo height={20} />
        </a>

        <div className="hidden md:flex items-center gap-5">
          {NAV_ITEMS.map((item) => {
            const active = currentPath === item.href;
            return (
              <a
                key={item.href}
                href={item.href}
                onClick={(e) => { e.preventDefault(); navigate(item.href); }}
                className={`text-sm transition-colors ${active ? "text-white" : "text-grey-400 hover:text-white"}`}
              >
                {t(item.key)}
              </a>
            );
          })}
          <button
            type="button"
            onClick={() => openAiOverlay()}
            data-ai-overlay-open="true"
            className="text-sm font-medium transition-all px-4 py-1.5 rounded-full border border-grey-600 text-white hover:border-white/50"
            style={{
              background: "linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)",
              backdropFilter: "blur(8px)",
              boxShadow: "0 0 0 0.5px rgba(255,255,255,0.06)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "linear-gradient(135deg, rgba(255,60,0,0.25) 0%, rgba(255,60,0,0.10) 100%)";
              e.currentTarget.style.borderColor = "rgba(255,60,0,0.5)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)";
              e.currentTarget.style.borderColor = "";
            }}
          >
            <span className="flex items-center gap-1.5">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" className="opacity-70">
                <path d="M8 1v4M8 11v4M1 8h4M11 8h4M3 3l2.5 2.5M10.5 10.5L13 13M13 3l-2.5 2.5M5.5 10.5L3 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              {tai("openAi")}
            </span>
          </button>
          <LanguageSwitcher />
        </div>

        <div className="md:hidden flex items-center">
          <button
            type="button"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            onClick={() => {
              setMenuOpen((v) => !v);
              setMenuIconKey((k) => k + 1);
              window.dispatchEvent(new CustomEvent("logo-dot-roll"));
            }}
            className="inline-flex items-center justify-center w-10 h-10 -mr-2 text-white"
          >
            <svg
              key={menuIconKey}
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden
              className={menuOpen ? "menu-icon-spin-left" : "menu-icon-spin-right"}
            >
              <path d="M 0 0 L 24 0 L 24 24 L 0 24 Z" />
            </svg>
          </button>
        </div>
      </div>

      <div
        aria-hidden={!menuOpen}
        className="md:hidden absolute left-0 right-0 top-14 overflow-hidden pointer-events-none"
        style={{
          maxHeight: menuOpen ? "80vh" : 0,
          opacity: menuOpen ? 1 : 0,
          transition: menuOpen
            ? "max-height 380ms cubic-bezier(0.22, 1, 0.36, 1), opacity 220ms ease-out"
            : "max-height 320ms cubic-bezier(0.4, 0, 0.2, 1), opacity 220ms ease-in 60ms",
        }}
      >
        <div
          className="px-5 py-6 flex flex-col gap-1 pointer-events-auto"
          style={{
            backgroundColor: "#16171C",
            borderBottom: "1px solid #27272a",
            transform: menuOpen ? "translateY(0)" : "translateY(-12px)",
            transition: menuOpen
              ? "transform 380ms cubic-bezier(0.22, 1, 0.36, 1)"
              : "transform 280ms cubic-bezier(0.4, 0, 1, 1)",
          }}
        >
          {NAV_ITEMS.map((item) => {
            const active = currentPath === item.href;
            return (
              <a
                key={item.href}
                href={item.href}
                tabIndex={menuOpen ? 0 : -1}
                onClick={(e) => { e.preventDefault(); navigate(item.href); }}
                className={`py-3 text-base transition-colors ${active ? "text-white" : "text-grey-400 hover:text-white"}`}
              >
                {t(item.key)}
              </a>
            );
          })}
          <button
            type="button"
            tabIndex={menuOpen ? 0 : -1}
            onClick={() => openAiOverlay()}
            data-ai-overlay-open="true"
            className="mt-2 text-sm font-medium transition-all px-5 py-2 rounded-full border border-grey-600 text-white self-start"
            style={{
              background: "linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)",
            }}
          >
            <span className="flex items-center gap-2">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" className="opacity-70">
                <path d="M8 1v4M8 11v4M1 8h4M11 8h4M3 3l2.5 2.5M10.5 10.5L13 13M13 3l-2.5 2.5M5.5 10.5L3 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              {tai("openAi")}
            </span>
          </button>
          <div className="pt-3 mt-2 border-t border-grey-800">
            <LanguageSwitcher />
          </div>
        </div>
      </div>
    </nav>
  );
}

function Hero() {
  const { t } = useTranslation("home");
  const { t: tc } = useTranslation("common");
  const { t: tai } = useTranslation("ai");
  const { t: tg } = useTranslation("overlayGate");
  const heroTitle = OVERLAY_ONLY ? tg("hero.title") : t("hero.title");
  const heroSubtitle = OVERLAY_ONLY ? tg("hero.subtitle") : t("hero.subtitle");
  return (
    <section
      className="relative px-4 sm:px-6 flex items-center overflow-hidden"
      style={{
        height: "90vh",
        backgroundColor: "#1B1D24",
        width: "100vw",
        marginLeft: "calc(-50vw + 50%)",
      }}
    >
      <img
        src="/assets/agency-profile.png"
        alt=""
        aria-hidden
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        style={{ objectPosition: "55% center" }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(to right, rgba(22,23,28,0.92) 0%, rgba(22,23,28,0.78) 45%, rgba(22,23,28,0.55) 100%)",
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(to bottom, rgba(22,23,28,0) 60%, rgba(22,23,28,0.85) 100%)",
        }}
      />

      <div className="relative w-full max-w-[900px] mx-auto px-5 sm:px-8 md:px-[66px] py-20 md:py-28">
        <h1
          className="fade-up text-4xl sm:text-5xl md:text-[64px] font-bold tracking-[-0.03em] leading-[1.05] text-white mb-6 max-w-3xl"
          style={{ animationDelay: "0.05s" }}
        >
          {heroTitle}
        </h1>

        <p
          className="fade-up text-base sm:text-lg text-grey-400 max-w-xl leading-relaxed mb-10"
          style={{ animationDelay: "0.15s" }}
        >
          {heroSubtitle}
        </p>

        <div
          className="fade-up flex flex-row flex-wrap items-center gap-3"
          style={{ animationDelay: "0.25s" }}
        >
          <a
            href="/contact"
            data-ai-overlay-query={tai("openContactPrompt")}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); openAiOverlayOrNavigate(tai("openContactPrompt"), "/contact"); }}
            className="inline-flex items-center gap-2 text-white text-sm font-medium px-7 py-2.5 rounded-full transition-colors"
            style={{ backgroundColor: "#FF3C00" }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#e63600")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#FF3C00")}
          >
            {tc("freeIntro")}
          </a>
          <a
            href="/pricing"
            data-ai-overlay-query={tai("openPricingPrompt")}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); openAiOverlayOrNavigate(tai("openPricingPrompt"), "/pricing"); }}
            className="inline-flex items-center gap-2 text-grey-200 text-sm font-medium px-7 py-2.5 rounded-full transition-colors border border-grey-700 hover:border-grey-500 hover:text-white"
          >
            {tc("seePricing")}
          </a>
        </div>
      </div>
    </section>
  );
}

function PageContent({ path }: { path: string }) {
  if (path === "/") return <Hero />;

  if (path.startsWith("/products/")) {
    const slug = path.replace("/products/", "");
    // Website rework isn't offered when the AI_OVERLAY_ONLY gate is on.
    if (OVERLAY_ONLY && slug === "website-rework") return <NotFound />;
    return <ProductPage slug={slug} />;
  }

  if (path.startsWith("/services/")) {
    const slug = path.replace("/services/", "");
    // Website rework isn't offered when the AI_OVERLAY_ONLY gate is on.
    if (OVERLAY_ONLY && slug === "website-rework") return <NotFound />;
    return <OfferingPage slug={slug} />;
  }

  const serviceMatch = ["/consulting", "/design", "/engineering"].find(s => path === s);
  if (serviceMatch) {
    return <ServicePage slug={serviceMatch.slice(1)} />;
  }

  switch (path) {
    case "/products":
      return <Products />;
    case "/about":
      return <About />;
    case "/contact":
      return <Contact />;
    case "/open-source":
      return <OpenSource />;
    case "/work":
      return <Work />;
    case "/stack":
      return <Stack />;
    case "/blog":
      return <Blog />;
    case "/careers":
      return <Careers />;
    case "/pricing":
      return <Pricing />;
    case "/impressum":
      return <Impressum />;
    case "/datenschutz":
      return <Datenschutz />;
    case "/skyline/privacy":
      return <SkylinePrivacy />;
    case "/skyline/terms":
      return <SkylineTerms />;
    case "/skyline/support":
      return <SkylineSupport />;
    default:
      return <NotFound />;
  }
}

export function App() {
  const path = useSyncExternalStore(subscribe, () => window.location.pathname);
  const isHome = path === "/";

  useEffect(() => {
    const scrollToTop = () => window.scrollTo(0, 0);
    scrollToTop();
    window.addEventListener("popstate", scrollToTop);
    return () => window.removeEventListener("popstate", scrollToTop);
  }, []);

  return (
    <>
      <Nav currentPath={path} />

      <div className="min-h-screen bg-grey-975 text-white relative">
        <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 5 }}>
          <div
            className="absolute top-0 bottom-0 left-4 md:left-14 w-px"
            style={{ background: "linear-gradient(to bottom, rgba(56,58,66,0) 0%, #383A42 100vh, #383A42 100%)" }}
          />
          <div
            className="absolute top-0 bottom-0 right-4 md:right-14 w-px"
            style={{ background: "linear-gradient(to bottom, rgba(56,58,66,0) 0%, #383A42 100vh, #383A42 100%)" }}
          />
        </div>
        <div className="hidden md:flex absolute inset-0 pointer-events-none justify-center" style={{ zIndex: 5 }}>
          <div className="w-full max-w-[900px] relative">
            <div
              className="absolute top-0 bottom-0 left-0 w-px"
              style={{ background: "linear-gradient(to bottom, rgba(56,58,66,0) 0%, #383A42 100vh, #383A42 100%)" }}
            />
            <div
              className="absolute top-0 bottom-0 right-0 w-px"
              style={{ background: "linear-gradient(to bottom, rgba(56,58,66,0) 0%, #383A42 100vh, #383A42 100%)" }}
            />
          </div>
        </div>
        {isHome ? (
          <main className="max-w-[900px] mx-auto w-full relative">
            <Hero />
            <FullDivider />
            <ProductsSection />
            <FullDivider />
            <CateSection />
            <FullDivider />
            <WhySection />
            <FullDivider />
            <HowSection />
            <FullDivider />
            <WhoSection />
            <FullDivider />
            <PricingTeaserSection />
            <FullDivider />
            <CtaSection />
            <FullDivider />
            <HomeFooter />
          </main>
        ) : (
          <main className="max-w-[900px] mx-auto w-full relative">
            <PageContent path={path} />
            <FullDivider />
            <HomeFooter />
          </main>
        )}
      </div>
    </>
  );
}

export default App;
